import { AllowedDurations, ValuePackTierTypes } from 'config/valuePackFormConfig';
import { DiscountTypes } from 'config/valuePackFormConfig';
import { SubscriptionTypes } from 'constants/subscriptionTypes';
import * as yup from 'yup';
import { getMissingRequiredFilters } from 'examples/ValuePack/components/requiredFormFilters';

// Custom filters set to "Required in the value pack form" must have a value
const requiredFilterValues = yup
  .mixed()
  .test('required-filters', 'Required field missing', function (value) {
    const missing = getMissingRequiredFilters(value);
    if (!missing.length) return true;
    return this.createError({
      message: `Please select ${missing.map((f) => f.label).join(', ')}`,
    });
  });

const validateSlug = (value) => {
  if (!value) return false;
  return /^[a-z0-9-]+$/.test(value);
};

const validateImageUrl = (value) => {
  if (!value) return true; // Allow empty
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

const validateImage = (value, context) => {
  const isEditMode = context?.options?.context?.isEditMode;
  
  // EDIT MODE: Images are completely optional
  if (isEditMode) return true;
  
  // CREATE MODE: Check if EITHER URL OR FILE exists
  const hasImageFile = context?.parent?.imageFile instanceof File;
  const hasBannerImageFile = context?.parent?.bannerImageFile instanceof File;
  const hasImageUrl = value && value.trim() !== '';
  
  // For imageUrl field
  if (context.path === 'imageUrl') {
    return hasImageUrl || hasImageFile; // EITHER URL OR FILE
  }
  
  // For bannerImageUrl field  
  if (context.path === 'bannerImageUrl') {
    return hasImageUrl || hasBannerImageFile; // EITHER URL OR FILE
  }
  
  return false;
};

const validateFutureDate = (value, context) => {
  if (!value) return true;
  
  const isEditMode = context?.options?.context?.isEditMode;
  if (isEditMode) return true;
  
  const date = new Date(value);
  const now = new Date();
  return date > now;
};

const validateExpiryAfterPublish = (value, context) => {
  if (!value || !context.parent.publishedAt) return true;
  const expiryDate = new Date(value);
  const publishDate = new Date(context.parent.publishedAt);
  return expiryDate > publishDate;
};

export const basicInfoSchema = yup.object().shape({
  name: yup
    .string()
    .required('Value Pack Name is required')
    .min(3, 'Name must be at least 3 characters')
    .max(100, 'Name must be less than 100 characters'),
  
  subTitle: yup
    .string()
    .required('Sub Title is required')
    .min(10, 'Sub title must be at least 10 characters')
    .max(100, 'Sub title must be less than 100 characters'),
  
  slug: yup
    .string()
    .required('URL Slug is required')
    .test('slug-format', 'Only lowercase letters, numbers and hyphens allowed', validateSlug),
  
  shortDescription: yup
    .string()
    .required('Short Description is required')
    .max(255, 'Short Description must be less than 255 characters'),
  
  description: yup
    .string()
    .required('Full Description is required')
    .min(50, 'Description should be at least 50 characters'),
  
  tags: yup
    .array()
    .of(yup.string())
    .min(1, 'At least one tag is required')
    .max(10, 'Maximum 10 tags allowed'),
  
  // CREATE MODE: Images are REQUIRED
  // imageUrl: yup
  //   .string()
  //   .required('Main image is required')
  //   .test('valid-url', 'Please enter a valid URL for the image', validateImageUrl),
  
  // bannerImageUrl: yup
  //   .string()
  //   .required('Banner image is required')
  //   .test('valid-url', 'Please enter a valid URL for the banner image', validateImageUrl),

  imageUrl: yup
    .string()
    .test('image-required', 'Main image is required (upload file OR enter URL)', function(value) {
      return validateImage(value, this);
    })
    .test('valid-url', 'Please enter a valid URL for the image', function(value) {
      if (!value) return true; // Allow empty if file exists
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    }),
  
  bannerImageUrl: yup
    .string()
    .test('banner-image-required', 'Banner image is required (upload file OR enter URL)', function(value) {
      return validateImage(value, this);
    })
    .test('valid-url', 'Please enter a valid URL for the banner image', function(value) {
      if (!value) return true; // Allow empty if file exists
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    }),

  publishedAt: yup
    .string()
    .nullable()
    .test('future-date', 'Publish date must be in the future', function(value) {
      return validateFutureDate(value, this);
    }),
  
  expiresAt: yup
    .string()
    .nullable()
    .test('future-date', 'Expiry date must be in the future', function(value) {
      return validateFutureDate(value, this);
    })
    .test('after-publish', 'Expiry date must be after publish date', function(value) {
      return validateExpiryAfterPublish(value, this);
    }),

  active: yup.boolean(),
  featured: yup.boolean()
});

export const createBasicInfoUpdateSchema = () => yup.object().shape({
  name: yup
    .string()
    .min(3, 'Name must be at least 3 characters')
    .max(100, 'Name must be less than 100 characters'),

  subTitle: yup
    .string()
    .min(10, 'Sub title must be at least 10 characters')
    .max(100, 'Sub title must be less than 100 characters'),
  
  slug: yup
    .string()
    .test('slug-format', 'Only lowercase letters, numbers and hyphens allowed', (value) => {
      if (!value) return true;
      return /^[a-z0-9-]+$/.test(value);
    }),
  
  shortDescription: yup
    .string()
    .max(255, 'Short Description must be less than 255 characters'),
  
  description: yup
    .string()
    .min(50, 'Description should be at least 50 characters'),
  
  tags: yup
    .array()
    .of(yup.string())
    .max(10, 'Maximum 10 tags allowed'),
  
  // EDIT MODE: Images are OPTIONAL - no required validation
  imageUrl: yup
    .string()
    .nullable()
    .test('valid-url', 'Please enter a valid URL for the image', (value) => {
      if (!value) return true; // Allow empty/null in edit mode
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    }),
  
  bannerImageUrl: yup
    .string()
    .nullable()
    .test('valid-url', 'Please enter a valid URL for the banner image', (value) => {
      if (!value) return true; // Allow empty/null in edit mode
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    }),
  
  publishedAt: yup
    .string()
    .nullable(),
    
  expiresAt: yup
    .string()
    .nullable()
    .test('after-publish', 'Expiry date must be after publish date', function(value) {
      return validateExpiryAfterPublish(value, this);
    }),

  active: yup.boolean(),
  featured: yup.boolean()
});

export const categoriesSchema = yup.object().shape({
  categories: yup
    .array()
    .of(
      yup.mixed().test(
        'is-valid-category',
        'Invalid category format',
        (value) => {
          // Allow both string IDs and category objects
          if (typeof value === 'string') return true;
          if (typeof value === 'object' && value?.id) return true;
          return false;
        }
      )
    )
    .min(2, 'Please select at least 2 categories')
    .max(5, 'Maximum 5 categories allowed')
    .test(
      'valid-categories-array',
      'Categories must be valid',
      function (value) {
        if (!Array.isArray(value)) return false;
        
        // ✅ Extract IDs for minimum count validation
        const categoryIds = value.map(item => 
          typeof item === 'string' ? item : item?.id
        ).filter(Boolean);
        
        return categoryIds.length >= 2;
      }
    ),
  
  // Not in the form any more (industries are a custom filter); kept optional
  targetIndustries: yup
    .array()
    .of(yup.string()),

  filterValues: requiredFilterValues
});

export const createCategoriesUpdateSchema = () => yup.object().shape({
  categories: yup
    .array()
    .of(
      yup.mixed().test(
        'is-valid-category',
        'Invalid category format',
        (value) => {
          // Allow both string IDs and category objects
          if (typeof value === 'string') return true;
          if (typeof value === 'object' && value?.id) return true;
          return false;
        }
      )
    )
    .max(5, 'Maximum 5 categories allowed')
    .test(
      'valid-categories-array',
      'Categories must be valid',
      function (value) {
        if (!value || !Array.isArray(value)) return true; // ✅ EDIT MODE: Allow empty
        
        // ✅ Extract IDs for validation
        const categoryIds = value.map(item => 
          typeof item === 'string' ? item : item?.id
        ).filter(Boolean);
        
        return categoryIds.length <= 5; // ✅ Only check max limit
      }
    ),
  
  targetIndustries: yup
    .array()
    .of(yup.string()),

  filterValues: requiredFilterValues
});

export const tierSchema = yup.object().shape({
  id: yup.string().nullable(),
  tierType: yup.string().oneOf(Object.values(ValuePackTierTypes)).required(),
  name: yup.string().required('Tier name is required'),
  description: yup.string().required('Tier description is required'),
  isPopular: yup.boolean(),
  
  // CHANGED: durationId instead of duration
  durationId: yup 
    .mixed()
    .required('Duration is required')
    .test('is-valid-duration', 'Please select a valid duration', (value) => {
      // Accept both string/number (ID) or object
      if (typeof value === 'string' || typeof value === 'number') return true;
      if (value && typeof value === 'object' && value.id) return true;
      return false;
    }),
  
  // CHANGED: countryId instead of countryCode
  countryId: yup
    .mixed()
    .required('Country is required')
    .test('is-valid-country', 'Please select a valid country', (value) => {
      // Accept both string/number (ID) or object
      if (typeof value === 'string' || typeof value === 'number') return true;
      if (value && typeof value === 'object' && value.id) return true;
      return false;
    }),
  
  originalPrice: yup
    .number()
    .transform((value, originalValue) => Number(originalValue))
    .required('Original price is required')
    .min(0, 'Original price cannot be negative'),
  currentPrice: yup
    .number()
    .typeError('Current price must be a number')
    .required('Current price is required')
    .min(0, 'Current price cannot be negative')
    .test(
      'current-price-validation',
      'Current price cannot be greater than original price',
      function(value) {
        const { originalPrice } = this.parent;
        return value <= originalPrice;
      }
    ),
  
  // Optional - matches backend (allowNull: true): a tier with no discount
  // legitimately has no discountType.
  discountType: yup
    .string()
    .nullable()
    .oneOf([...Object.values(DiscountTypes), null, ''], 'Please select a valid discount type'),
  flatDiscount: yup
    .number()
    .transform((value, originalValue) => Number(originalValue))
    .min(0)
    .test('flat-discount-validation', 'Flat discount cannot exceed original price', function (value) {
      const { originalPrice, discountType } = this.parent;
      if (discountType === DiscountTypes.FLAT && value > originalPrice) {
        return false;
      }
      return true;
    }),
  percentageDiscount: yup
    .number()
    .min(0, 'Percentage discount cannot be negative')
    .max(100, 'Percentage discount cannot exceed 100%'),
    
  categoryFeatures: yup.array().of(
    yup.object().shape({
      categoryId: yup.string().required(),
      globalFeatureId: yup.string().required()
    })
  ),
  additionalFeatures: yup.array().of(
    yup.object().shape({
      categoryId: yup.string().required(),
      additionalFeatureId: yup.string().required()
    })
  )
});

const isNewValuePack = (value, context) => {
  return !context.parent.id || context.parent.id.startsWith('temp-');
};

export const tiersSchema = yup.object().shape({
  tiers: yup
    .array()
    .of(tierSchema)
    .test('tiers-required-for-existing', 'At least one tier is required', function(tiers) {
      if (isNewValuePack(tiers, this)) return true;
      return tiers && tiers.length > 0;
    })
    .test('has-popular-tier-if-tiers-exist', 'At least one tier should be marked as popular', (tiers) => {
      if (!tiers || tiers.length === 0) return true;
      return tiers.some(tier => tier.isPopular);
    })
    .test('unique-tier-types', 'Each tier type can only appear once per duration/country', function(tiers) {
      if (!tiers || tiers.length === 0) return true;
      
      const tierGroups = {};
      
      for (const tier of tiers) {
        // CHANGED: Use durationId and countryId
        if (tier.durationId && tier.countryId) {
          // Extract IDs from objects if needed
          const durationId = typeof tier.durationId === 'object' ? tier.durationId.id : tier.durationId;
          const countryId = typeof tier.countryId === 'object' ? tier.countryId.id : tier.countryId;
          
          const key = `${durationId}-${countryId}`;
          if (!tierGroups[key]) {
            tierGroups[key] = new Set();
          }
          if (tierGroups[key].has(tier.tierType)) {
            return this.createError({
              message: `Duplicate ${tier.tierType} tier for this duration and country combination`
            });
          }
          tierGroups[key].add(tier.tierType);
        }
      }
      
      return true;
    })
});

export const validateTiers = (data) => {
  return tiersSchema.validate(data, { abortEarly: false });
};

export const createValidationResolver = (stepIndex) => {
  const schema = getValidationSchemaByStep(stepIndex);
  return (values) => {
    try {
      schema.validateSync(values, { abortEarly: false });
      return { values, errors: {} };
    } catch (validationErrors) {
      const errors = {};
      if (validationErrors.inner) {
        validationErrors.inner.forEach(error => {
          errors[error.path] = { message: error.message, type: 'validation' };
        });
      }
      return { values: {}, errors };
    }
  };
};

// NEW -------------------------
// export const subscriptionTypeSchema = yup.object().shape({
//   subscriptionType: yup
//     .string()
//     .oneOf(Object.values(SubscriptionTypes))
//     .required('Subscription type is required')
// });

export const subscriptionTypeSchema = yup.object().shape({
  subscriptionType: yup
    .string()
    .required('Subscription type is required')
    .oneOf(
      Object.values(SubscriptionTypes), 
      'Please select a valid subscription type'
    )
    .test(
      'not-empty-string',
      'Subscription type is required',
      (value) => {
        // Ensure it's not just an empty string
        return value && value.trim().length > 0;
      }
    )
});

// For EDIT mode (optional subscription type)
export const createSubscriptionTypeUpdateSchema = () => yup.object().shape({
  subscriptionType: yup
    .string()
    .nullable()
    .oneOf(
      [...Object.values(SubscriptionTypes), null, ''], 
      'Please select a valid subscription type'
    )
});

export const durationCountrySchema = yup.object().shape({
  selectedDuration: yup
    .number()
    .required('Duration is required')
    .oneOf(AllowedDurations, 'Please select a valid duration'),
  selectedCountry: yup
    .string()
    .required('Country is required')
});

export const getValidationSchemaByStep = (stepIndex, isEditMode = false) => {  
  if (isEditMode) {
    switch (stepIndex) {
      case 0:
        return createBasicInfoUpdateSchema();
      case 1:
        return createCategoriesUpdateSchema();
      case 2:
        return tiersSchema;
      default:
        return createBasicInfoUpdateSchema();
    }
  } else {
    // Create mode - strict validation
    switch (stepIndex) {
      case 0:
        return basicInfoSchema;
      case 1:
        return categoriesSchema;
      case 2:
        return tiersSchema;
      default:
        return basicInfoSchema;
    }
  }
};

// export const getValidationSchemaByStep = (stepIndex, isEditMode = false) => {  
//   if (isEditMode) {
//     // Use FLEXIBLE schemas for edit mode
//     switch (stepIndex) {
//       case 0:
//         return createBasicInfoUpdateSchema();
//       case 1:
//         return createCategoriesUpdateSchema();
//       case 2:
//         return subscriptionTypeSchema;
//       case 3:
//         return durationCountrySchema;
//       case 4:
//         return tiersSchema;
//       default:
//         return createBasicInfoUpdateSchema();
//     }
//   } else {
//     // Use STRICT schemas for create mode
//     switch (stepIndex) {
//       case 0:
//         return basicInfoSchema;
//       case 1:
//         return categoriesSchema;
//       case 2:
//         return subscriptionTypeSchema;
//       case 3:
//         return durationCountrySchema;
//       case 4:
//         return tiersSchema;
//       default:
//         return basicInfoSchema;
//     }
//   }
// };


// OLD --------------------------------------------------
// export const getValidationSchemaByStep = (stepIndex, isEditMode = false) => {  
//   if (isEditMode) {
//     switch (stepIndex) {
//       case 0:
//         return createBasicInfoUpdateSchema();
//       case 1:
//         return createCategoriesUpdateSchema();
//       case 2:
//         return tiersSchema;
//       default:
//         return createBasicInfoUpdateSchema();
//     }
//   } else {
    
//     switch (stepIndex) {
//       case 0:
//         return basicInfoSchema;
//       case 1:
//         return categoriesSchema;
//       case 2:
//         return tiersSchema;
//       default:
//         return basicInfoSchema;
//     }
//   }
// };

// export const tierSchema = yup.object().shape({
//   id: yup.string().nullable(),
//   tierType: yup.string().oneOf(Object.values(ValuePackTierTypes)).required(),
//   name: yup.string().required('Tier name is required'),
//   description: yup.string().required('Tier description is required'),
//   isPopular: yup.boolean(),
//   duration: yup.number().required('Duration is required').oneOf(AllowedDurations),
  
//   countryCode: yup.string().required('Country code is required'),
//   originalPrice: yup
//     .number()
//     .transform((value, originalValue) => Number(originalValue))
//     .required('Original price is required')
//     .min(0, 'Original price cannot be negative'),
//   currentPrice: yup
//     .number()
//     .typeError('Current price must be a number')
//     .required('Current price is required')
//     .min(0, 'Current price cannot be negative')
//     .test(
//       'current-price-validation',
//       'Current price cannot be greater than original price',
//       function(value) {
//         const { originalPrice } = this.parent;
//         return value <= originalPrice;
//       }
//     ),
  
//   discountType: yup.string().oneOf(Object.values(DiscountTypes)).required(),
//   flatDiscount: yup
//     .number()
//     .transform((value, originalValue) => Number(originalValue))
//     .min(0)
//     .test('flat-discount-validation', 'Flat discount cannot exceed original price', function (value) {
//       const { originalPrice, discountType } = this.parent;
//       if (discountType === DiscountTypes.FLAT && value > originalPrice) {
//         return false;
//       }
//       return true;
//     }),
//   percentageDiscount: yup
//     .number()
//     .min(0, 'Percentage discount cannot be negative')
//     .max(100, 'Percentage discount cannot exceed 100%'),
    
//   categoryFeatures: yup.array().of(
//     yup.object().shape({
//       categoryId: yup.string().required(),
//       globalFeatureId: yup.string().required()
//     })
//   ),
//   additionalFeatures: yup.array().of(
//     yup.object().shape({
//       categoryId: yup.string().required(),
//       additionalFeatureId: yup.string().required()
//     })
//   )
// });

// const isNewValuePack = (value, context) => {
//   return !context.parent.id || context.parent.id.startsWith('temp-');
// };

// export const tiersSchema = yup.object().shape({
//   tiers: yup
//     .array()
//     .of(tierSchema)
//     .test('tiers-required-for-existing', 'At least one tier is required', function(tiers) {
//       if (isNewValuePack(tiers, this)) return true;
//       return tiers && tiers.length > 0;
//     })
//     .test('has-popular-tier-if-tiers-exist', 'At least one tier should be marked as popular', (tiers) => {
//       if (!tiers || tiers.length === 0) return true;
//       return tiers.some(tier => tier.isPopular);
//     })
//     .test('unique-tier-types', 'Each tier type can only appear once per duration/country', function(tiers) {
//       if (!tiers || tiers.length === 0) return true;
      
//       const tierGroups = {};
      
//       for (const tier of tiers) {
//         if (tier.countryPricing) {
//           for (const country of Object.keys(tier.countryPricing)) {
//             const key = `${tier.duration}-${country}`;
//             if (!tierGroups[key]) {
//               tierGroups[key] = new Set();
//             }
//             if (tierGroups[key].has(tier.tierType)) {
//               return this.createError({
//                 message: `Duplicate ${tier.tierType} tier for ${tier.duration} months in ${country}`
//               });
//             }
//             tierGroups[key].add(tier.tierType);
//           }
//         }
//       }
      
//       return true;
//     })
// });

// export const validateTiers = (data) => {
//   return tiersSchema.validate(data, { abortEarly: false });
// };

